import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, fileUrl } from "@/lib/api";
import { Download, Users, Truck, UserRound, FileBarChart2, CalendarDays } from "lucide-react";

// Snap an ISO date to the Monday of that week and the Sunday of that week —
// UK ops report on Mon-Sun weeks.
function weekBounds(iso) {
  if (!iso) return { from: "", to: "" };
  const d = new Date(iso + "T00:00:00");
  const dow = (d.getDay() + 6) % 7;      // Mon=0 … Sun=6
  const mon = new Date(d); mon.setDate(d.getDate() - dow);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const fmt = (x) => x.toISOString().slice(0, 10);
  return { from: fmt(mon), to: fmt(sun) };
}

// Small helpers defined outside the page component so React doesn't recreate
// them on every render (which would blow away the download link DOM and its
// focus state).
function DownloadBtn({ href, disabled, testid, children }) {
  if (disabled) {
    return (
      <span
        data-testid={testid}
        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-sm border border-slate-200 text-slate-400 bg-slate-50 cursor-not-allowed"
      >
        <Download className="w-3.5 h-3.5" /> {children}
      </span>
    );
  }
  return (
    <a
      data-testid={testid}
      href={href}
      download
      rel="noopener"
      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-sm border border-slate-900 bg-slate-900 text-white hover:bg-slate-800"
    >
      <Download className="w-3.5 h-3.5" /> {children}
    </a>
  );
}

function SectionCard({ icon: Icon, tag, title, blurb, accent, children }) {
  return (
    <Card className={`rounded-sm border ${accent} p-5 space-y-4`}>
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-sm bg-white/70 border border-current/10 shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="text-[10.5px] uppercase tracking-[0.2em] font-semibold opacity-70">{tag}</div>
          <h3 className="font-semibold text-base leading-tight">{title}</h3>
          <p className="text-xs opacity-80 mt-1 leading-relaxed">{blurb}</p>
        </div>
      </div>
      {children}
    </Card>
  );
}

// Interactive Profit reports. Each section (except the full report) reveals
// dropdowns so ops can narrow the download to a specific client / contact
// before opening the PDF.
export default function FinanceProfitReports() {
  const [clients, setClients] = useState([]);
  const [subs, setSubs] = useState([]);
  const [selfEmp, setSelfEmp] = useState([]);

  const [generalClient, setGeneralClient] = useState("__all");
  const [subContactId, setSubContactId] = useState("");
  const [subClientId, setSubClientId] = useState("__all");
  const [selfContactId, setSelfContactId] = useState("");
  const [selfClientId, setSelfClientId] = useState("__all");

  // Weekly profit — pick a day, we snap to that Mon-Sun. Optionally by client.
  const [weekDate, setWeekDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [weekClient, setWeekClient] = useState("__all");

  useEffect(() => {
    (async () => {
      try {
        // Legacy schema: contact `type=customer` are Subcontractor companies,
        // contact `type=subcontractor` are Self-employed drivers.
        const [cl, subsRes, selfRes] = await Promise.all([
          api.get("/clients"),
          api.get("/contacts", { params: { type: "customer" } }),
          api.get("/contacts", { params: { type: "subcontractor" } }),
        ]);
        setClients(cl.data || []);
        setSubs((subsRes.data || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")));
        setSelfEmp((selfRes.data || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      } catch (e) { /* silent — full-report card still works without contacts */ }
    })();
  }, []);

  const buildHref = (params) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v && v !== "__all") qs.set(k, v);
    });
    const suffix = qs.toString();
    return fileUrl(`/finance/profit-report.pdf${suffix ? `?${suffix}` : ""}`);
  };

  return (
    <div data-testid="finance-profit-reports-page" className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-emerald-700 font-semibold">Payable · Profit</div>
        <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">Profit reports</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Download a Charge vs Pay comparison PDF. Pick a scope, then optionally narrow to a specific client or contact.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Weekly profit — Mon-Sun window computed from any picked date. */}
        <SectionCard
          icon={CalendarDays}
          tag="Week snapshot"
          title="Full week profit"
          blurb="Charge vs Pay for every job (standard + DSP) inside a Mon-Sun window. Pick any day and we'll snap to that week."
          accent="border-emerald-300 bg-emerald-50 text-emerald-900 md:col-span-2"
        >
          {(() => {
            const b = weekBounds(weekDate);
            const qs = new URLSearchParams({ date_from: b.from, date_to: b.to });
            if (weekClient && weekClient !== "__all") qs.set("client_id", weekClient);
            const href = fileUrl(`/finance/profit-weekly.pdf?${qs.toString()}`);
            return (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-emerald-900/80">Any day in the week</Label>
                    <Input
                      data-testid="week-date"
                      type="date"
                      value={weekDate}
                      onChange={(e) => setWeekDate(e.target.value)}
                      className="mt-1 rounded-sm border-emerald-300 bg-white/70"
                    />
                    <p className="text-[10.5px] text-emerald-900/70 mt-1">
                      Week: <span className="font-medium">{b.from}</span> → <span className="font-medium">{b.to}</span>
                    </p>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-emerald-900/80">Client (optional)</Label>
                    <Select value={weekClient} onValueChange={setWeekClient}>
                      <SelectTrigger data-testid="week-client-select" className="mt-1 rounded-sm border-emerald-300 bg-white/70">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all">All clients</SelectItem>
                        {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end">
                  <DownloadBtn testid="week-download" disabled={!b.from} href={href}>
                    Download weekly profit PDF
                  </DownloadBtn>
                </div>
              </>
            );
          })()}
        </SectionCard>

        {/* General clients */}
        <SectionCard
          icon={Users}
          tag="Unassigned"
          title="General clients"
          blurb="Compare Charge vs Pay using General (unassigned) baseline pay rates. Pick a client to focus on, or leave as All clients."
          accent="border-amber-200 bg-amber-50 text-amber-900"
        >
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-amber-800/80">Client</Label>
            <Select value={generalClient} onValueChange={setGeneralClient}>
              <SelectTrigger data-testid="general-client-select" className="mt-1 rounded-sm border-amber-300 bg-white/70">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All clients</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end">
            <DownloadBtn
              testid="general-download"
              href={buildHref({ scope: "general", client_id: generalClient })}
            >
              Download PDF
            </DownloadBtn>
          </div>
        </SectionCard>

        {/* Subcontractors */}
        <SectionCard
          icon={Truck}
          tag="Companies"
          title="Subcontractors"
          blurb="Pick a subcontractor company, then optionally narrow to a specific client — or leave as All clients."
          accent="border-slate-200 bg-white text-slate-900"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Subcontractor</Label>
              <Select value={subContactId} onValueChange={setSubContactId}>
                <SelectTrigger data-testid="sub-contact-select" className="mt-1 rounded-sm border-slate-300">
                  <SelectValue placeholder={subs.length === 0 ? "No subs on file" : "Pick a subcontractor"} />
                </SelectTrigger>
                <SelectContent>
                  {subs.length === 0
                    ? <SelectItem value="__none" disabled>No subcontractors</SelectItem>
                    : subs.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Client</Label>
              <Select value={subClientId} onValueChange={setSubClientId}>
                <SelectTrigger data-testid="sub-client-select" className="mt-1 rounded-sm border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All clients</SelectItem>
                  {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end">
            <DownloadBtn
              testid="sub-download"
              disabled={!subContactId}
              href={buildHref({ scope: "subcontractor", contact_id: subContactId, client_id: subClientId })}
            >
              Download PDF
            </DownloadBtn>
          </div>
        </SectionCard>

        {/* Self-employed drivers */}
        <SectionCard
          icon={UserRound}
          tag="Drivers"
          title="Self-employed drivers"
          blurb="Pick a self-employed driver, then optionally narrow to a specific client — or leave as All clients."
          accent="border-slate-200 bg-white text-slate-900"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Driver</Label>
              <Select value={selfContactId} onValueChange={setSelfContactId}>
                <SelectTrigger data-testid="self-contact-select" className="mt-1 rounded-sm border-slate-300">
                  <SelectValue placeholder={selfEmp.length === 0 ? "No drivers on file" : "Pick a driver"} />
                </SelectTrigger>
                <SelectContent>
                  {selfEmp.length === 0
                    ? <SelectItem value="__none" disabled>No self-employed drivers</SelectItem>
                    : selfEmp.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Client</Label>
              <Select value={selfClientId} onValueChange={setSelfClientId}>
                <SelectTrigger data-testid="self-client-select" className="mt-1 rounded-sm border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All clients</SelectItem>
                  {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end">
            <DownloadBtn
              testid="self-download"
              disabled={!selfContactId}
              href={buildHref({ scope: "self_employed", contact_id: selfContactId, client_id: selfClientId })}
            >
              Download PDF
            </DownloadBtn>
          </div>
        </SectionCard>

        {/* Everything */}
        <SectionCard
          icon={FileBarChart2}
          tag="Full report"
          title="Everything"
          blurb="Full profit analysis across every client, site and pay row. Best for month-end reviews."
          accent="border-emerald-300 bg-emerald-50 text-emerald-900"
        >
          <div className="flex justify-end">
            <DownloadBtn
              testid="all-download"
              href={buildHref({})}
            >
              Download PDF
            </DownloadBtn>
          </div>
        </SectionCard>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none p-5 bg-slate-50">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5">How this works</div>
        <ul className="text-sm text-slate-700 space-y-1.5 list-disc pl-5">
          <li>Reports pair each site&apos;s Charge rate against its Pay rates and compute the per-line profit + margin.</li>
          <li>Filters cascade — pick a scope, then a contact, then a client — the PDF only shows matching rows.</li>
          <li>Every PDF is stamped with the date, time, and the user who generated it in the footer.</li>
        </ul>
      </Card>
    </div>
  );
}
